import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getAllNotifications,
  getUnreadNotificationsCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  Notification,
  PaginatedNotificationResponse,
} from "../../services/NotificationServices/notificationService";

interface NotificationsState {
  items: Notification[];
  unreadCount: number;
  loading: boolean;
  loadingUnread: boolean;
  error?: string;
  pageNo: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  dropdownLoaded: boolean;
}

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  loading: false,
  loadingUnread: false,
  pageNo: 1,
  pageSize: 10,
  totalPages: 0,
  totalCount: 0,
  dropdownLoaded: false,
};

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchList",
  async (
    { pageNo = 1, pageSize = 10 }: { pageNo?: number; pageSize?: number },
    { rejectWithValue }
  ) => {
    try {
      return await getAllNotifications(pageNo, pageSize);
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to load notifications");
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  "notifications/fetchUnread",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUnreadNotificationsCount();
      return response.unreadCount; // Changed from response.count to response.unreadCount
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to load unread count");
    }
  }
);

export const markOneAsRead = createAsyncThunk(
  "notifications/markOne",
  async (notificationId: number, { rejectWithValue }) => {
    try {
      await markNotificationAsRead(notificationId);
      return notificationId;
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to mark as read");
    }
  }
);

export const markAllAsReadThunk = createAsyncThunk(
  "notifications/markAll",
  async (_, { rejectWithValue }) => {
    try {
      await markAllNotificationsAsRead();
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to mark all as read");
    }
  }
);

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    prependNotification(state, action: PayloadAction<Notification>) {
      if (!state.items.find(n => n.notificationId === action.payload.notificationId)) {
        state.items.unshift(action.payload);
      }
      if (!action.payload.isRead) {
        state.unreadCount += 1;
      }
      if (state.items.length > 50) {
        state.items = state.items.slice(0, 50);
      }
    },
    setDropdownLoaded(state, action: PayloadAction<boolean>) {
      state.dropdownLoaded = action.payload;
    },
    /** Mark a single notification read locally (triggered by SignalR NotificationRead event) */
    markOneReadLocal(state, action: PayloadAction<number>) {
      const target = state.items.find(n => n.notificationId === action.payload);
      if (target && !target.isRead) {
        target.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    /** Mark all notifications read locally (triggered by SignalR AllNotificationsRead event) */
    markAllReadLocal(state) {
      state.items.forEach(n => (n.isRead = true));
      state.unreadCount = 0;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchNotifications.pending, state => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        fetchNotifications.fulfilled,
        (state, action: PayloadAction<PaginatedNotificationResponse>) => {
          state.loading = false;
          state.items = action.payload.data;
          state.pageNo = action.payload.pageNo;
          state.pageSize = action.payload.pageSize;
          state.totalPages = action.payload.totalPages;
          state.totalCount = action.payload.totalCount;
          state.dropdownLoaded = true;
        }
      )
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message;
      })
      .addCase(fetchUnreadCount.pending, state => {
        state.loadingUnread = true;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.loadingUnread = false;
        state.unreadCount = action.payload;
      })
      .addCase(fetchUnreadCount.rejected, state => {
        state.loadingUnread = false;
      })
      .addCase(markOneAsRead.fulfilled, (state, action) => {
        const notificationId = action.payload;
        const target = state.items.find(n => n.notificationId === notificationId);
        if (target && !target.isRead) {
          target.isRead = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllAsReadThunk.fulfilled, state => {
        state.items.forEach(n => (n.isRead = true));
        state.unreadCount = 0;
      });
  },
});

export const { prependNotification, setDropdownLoaded, markOneReadLocal, markAllReadLocal } = notificationsSlice.actions;
export default notificationsSlice.reducer;